/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import React, { useEffect, useState } from 'react';

import useRouter from 'js/lib/useRouter';

import type { Theme } from '@coursera/cds-core';
import { SelectOption, SilentSelectField } from '@coursera/cds-core';

import A11yScreenReaderOnly from 'bundles/a11y/components/A11yScreenReaderOnly';
import { answerSorts, loadingStates } from 'bundles/discussions/constants';
import type { AnswerSort, LoadingState } from 'bundles/discussions/constants';

import _t from 'i18n!nls/discussions';

const { oldestSort, popularSort, newestSort } = answerSorts;

export type Props = {
  sort: AnswerSort;
  loadingState: LoadingState;
};

const styles = {
  selectField: (theme: Theme) =>
    css({
      margin: theme.spacing(24, 0, 12, 24),
    }),
};

const GradedDiscussionPromptAnswersSortBar = ({ sort, loadingState }: Props) => {
  const router = useRouter();
  const [sortNotification, setSortNotification] = useState('');

  useEffect(() => {
    const answerSortsText = {
      [answerSorts.oldestSort]: _t('earliest'),
      [answerSorts.popularSort]: _t('top'),
      [answerSorts.newestSort]: _t('most recent'),
    };

    if (loadingState === loadingStates.DONE) {
      setSortNotification(
        _t('Submissions are sorted by #{sort}', {
          sort: answerSortsText[sort],
        })
      );
    }

    if (loadingState === loadingStates.LOADING) {
      setSortNotification('');
    }
  }, [loadingState, sort]);

  const handleSortChange = (
    event: React.ChangeEvent<{
      name?: string;
      value: unknown;
    }>
  ) => {
    router.push({
      pathname: router.location.pathname,
      params: router.params,
      query: Object.assign(router.location.query, { sort: event.target.value }),
    });
  };

  return (
    <div className="graded-discussion-prompt-replies-sort-dropdown">
      <SilentSelectField
        id="answer-sort-select"
        label={_t('Sort by')}
        value={sort}
        onChange={handleSortChange}
        css={styles.selectField}
      >
        <SelectOption value={oldestSort}>{_t('Earliest')}</SelectOption>
        <SelectOption value={popularSort}>{_t('Top')}</SelectOption>
        <SelectOption value={newestSort}>{_t('Most Recent')}</SelectOption>
      </SilentSelectField>

      <A11yScreenReaderOnly tagName="span" role="status" aria-live="polite">
        {sortNotification}
      </A11yScreenReaderOnly>
    </div>
  );
};

export default GradedDiscussionPromptAnswersSortBar;
